import { externalJobsKeys } from '@/hooks/use-external-jobs'
import {
  GENERATING_TOPICS_POLL_INTERVAL_MS,
  hasAnyGeneratingTopics,
  isTopicGenerating,
} from '@/lib/learning-generation-limits'
import {
  failedTopicRecoveryKey,
  skillNameFromLearningTopicTitle,
} from '@/lib/learning-topic-recovery'
import { learningService } from '@/services/learning.service'
import type {
  BookCategory,
  ContextPosition,
  LearningTopicDetail,
  LearningTopicListResponse,
  LearningTopicWithProgress,
  PodcastCategory,
  SkillProfileUpdate,
  UserLearningProgressUpdate,
} from '@/types'
import {
  type Query,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'

export const learningKeys = {
  all: ['learning'] as const,
  profile: () => [...learningKeys.all, 'profile'] as const,
  topics: () => [...learningKeys.all, 'topics'] as const,
  topicList: (page: number, limit: number, include_popular: boolean) =>
    [...learningKeys.topics(), page, limit, include_popular] as const,
  topicDetail: (id: number) => [...learningKeys.topics(), 'detail', id] as const,
  modules: () => [...learningKeys.all, 'modules'] as const,
  module: (id: number) => [...learningKeys.modules(), id] as const,
  progress: () => [...learningKeys.all, 'progress'] as const,
  moduleProgress: (id: number) => [...learningKeys.progress(), 'module', id] as const,
  weeklyProgress: () => [...learningKeys.all, 'weekly-progress'] as const,
  recommended: (limit: number) => [...learningKeys.all, 'recommended', limit] as const,
  qa: () => [...learningKeys.all, 'qa'] as const,
  moduleQA: (moduleId: number) => [...learningKeys.qa(), moduleId] as const,
  notes: () => [...learningKeys.all, 'notes'] as const,
  moduleNote: (moduleId: number) => [...learningKeys.notes(), moduleId] as const,
  books: () => [...learningKeys.all, 'books'] as const,
  bookList: (category?: BookCategory) => [...learningKeys.books(), 'list', category] as const,
  bookDetail: (id: number) => [...learningKeys.books(), 'detail', id] as const,
  podcasts: () => [...learningKeys.all, 'podcasts'] as const,
  podcastList: (category?: PodcastCategory) =>
    [...learningKeys.podcasts(), 'list', category] as const,
  podcastDetail: (id: number) => [...learningKeys.podcasts(), 'detail', id] as const,
}

export function useTopics(page = 1, limit = 50, include_popular = true) {
  return useQuery({
    queryKey: learningKeys.topicList(page, limit, include_popular),
    queryFn: () => learningService.listTopics(page, limit, include_popular),
    refetchInterval: (query: Query<LearningTopicListResponse, Error>) =>
      hasAnyGeneratingTopics(query.state.data?.items) ? GENERATING_TOPICS_POLL_INTERVAL_MS : false,
  })
}

export function useArchiveJobTopics() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobPostingId: number) => learningService.archiveJobTopics(jobPostingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
    },
  })
}

export function useArchiveLearnTopic() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (topicId: number) => learningService.archiveTopic(topicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
    },
  })
}

export function useTopic(topicId: number) {
  return useQuery({
    queryKey: learningKeys.topicDetail(topicId),
    queryFn: () => learningService.getTopic(topicId),
    enabled: Number.isFinite(topicId) && topicId > 0,
    refetchInterval: (query: Query<LearningTopicDetail, Error>) =>
      query.state.data && isTopicGenerating(query.state.data.generation_status)
        ? GENERATING_TOPICS_POLL_INTERVAL_MS
        : false,
  })
}

export function useRetryTopicGeneration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (topicId: number) => learningService.retryTopicGeneration(topicId),
    onSuccess: (_data, topicId) => {
      queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
      if (Number.isFinite(topicId) && topicId > 0) {
        queryClient.invalidateQueries({ queryKey: learningKeys.topicDetail(topicId) })
      }
    },
  })
}

export function useModule(moduleId: number) {
  return useQuery({
    queryKey: learningKeys.module(moduleId),
    queryFn: () => learningService.getModule(moduleId),
    enabled: Number.isFinite(moduleId) && moduleId > 0,
  })
}

export function useOverallProgress() {
  return useQuery({
    queryKey: learningKeys.progress(),
    queryFn: () => learningService.getProgress(),
  })
}

export function useUpdateProgress() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      moduleId,
      data,
    }: {
      moduleId: number
      data?: UserLearningProgressUpdate
    }) => learningService.updateProgress(moduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.progress() })
      queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
      queryClient.invalidateQueries({ queryKey: learningKeys.modules() })
    },
  })
}

export function useUpdateProgressForTopic(topicId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      moduleId,
      data,
    }: {
      moduleId: number
      data?: UserLearningProgressUpdate
    }) => learningService.updateProgress(moduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.progress() })
      queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
      queryClient.invalidateQueries({ queryKey: learningKeys.modules() })
      if (Number.isFinite(topicId) && topicId > 0) {
        queryClient.invalidateQueries({ queryKey: learningKeys.topicDetail(topicId) })
      }
    },
  })
}

export function useModuleProgress(moduleId: number) {
  return useQuery({
    queryKey: learningKeys.moduleProgress(moduleId),
    queryFn: () => learningService.getModuleProgress(moduleId),
    enabled: Number.isFinite(moduleId) && moduleId > 0,
  })
}

export function useWeeklyProgress() {
  return useQuery({
    queryKey: learningKeys.weeklyProgress(),
    queryFn: () => learningService.getWeeklyProgress(),
  })
}

export function useSkillProfile() {
  return useQuery({
    queryKey: learningKeys.profile(),
    queryFn: () => learningService.getProfile(),
  })
}

export function useUpdateSkillProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SkillProfileUpdate) => learningService.updateProfile(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: learningKeys.profile() })
      // When target_role changes, job recommendations need to refresh
      if (variables.target_role) {
        queryClient.invalidateQueries({ queryKey: externalJobsKeys.recommended() })
        queryClient.invalidateQueries({ queryKey: externalJobsKeys.lists() })
      }
    },
  })
}

export function useInitializeLearning() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => learningService.initializeLearning(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.all })
    },
  })
}

export function useRecommendedTopics(limit = 5) {
  return useQuery({
    queryKey: learningKeys.recommended(limit),
    queryFn: () => learningService.getRecommended(limit),
  })
}

export function useImportResumeSkills() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => learningService.importResumeSkills(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.profile() })
    },
  })
}

export function useRefreshLearningContext() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => learningService.refreshContext(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.profile() })
    },
  })
}

/**
 * When list data includes personalized topics stuck in `generation_status === 'failed'`,
 * archive then re-queue generation once per logical skill (job + skill); on a second
 * failure, archive only so the row disappears from the API.
 */
export function useFailedTopicAutoRecovery(topics: LearningTopicWithProgress[]) {
  const queryClient = useQueryClient()
  const phaseBySkillKey = useRef<Record<string, number>>({})
  const inFlight = useRef<Set<string>>(new Set())

  const failedSig = useMemo(() => {
    return topics
      .filter((t) => t.generation_status === 'failed' && !t.is_popular)
      .map((t) => {
        const skill = skillNameFromLearningTopicTitle(t.title) ?? ''
        return `${t.id}|${skill}|${t.job_posting_id ?? ''}`
      })
      .sort()
      .join(';')
  }, [topics])

  useEffect(() => {
    if (!failedSig) return

    const failed = topics.filter((t) => t.generation_status === 'failed' && !t.is_popular)

    for (const topic of failed) {
      const skill = skillNameFromLearningTopicTitle(topic.title)
      if (!skill) continue
      const key = failedTopicRecoveryKey(skill, topic.job_posting_id)
      const n = phaseBySkillKey.current[key] ?? 0
      if (n >= 2) continue
      if (inFlight.current.has(key)) continue

      inFlight.current.add(key)
      void (async () => {
        try {
          if (n === 0) {
            await learningService.archiveTopic(topic.id)
            const genOpts: {
              jobContext?: string
              jobPostingId?: number
            } = {}
            if (topic.job_context) genOpts.jobContext = topic.job_context
            if (topic.job_posting_id != null) genOpts.jobPostingId = topic.job_posting_id
            await learningService.generateTopic(skill, genOpts)
            phaseBySkillKey.current[key] = 1
            queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
          } else if (n === 1) {
            await learningService.archiveTopic(topic.id)
            phaseBySkillKey.current[key] = 2
            queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
          }
        } catch {
          phaseBySkillKey.current[key] = 2
          queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
        } finally {
          inFlight.current.delete(key)
        }
      })()
    }
  }, [failedSig, topics, queryClient])
}

export function useGenerateTopic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      skillName: string
      currentLevel?: string
      targetLevel?: string
      timeAvailable?: string
      jobContext?: string
      jobPostingId?: number
    }) => {
      const options: {
        currentLevel?: string
        targetLevel?: string
        timeAvailable?: string
        jobContext?: string
        jobPostingId?: number
      } = {}
      if (params.currentLevel) options.currentLevel = params.currentLevel
      if (params.targetLevel) options.targetLevel = params.targetLevel
      if (params.timeAvailable) options.timeAvailable = params.timeAvailable
      if (params.jobContext) options.jobContext = params.jobContext
      if (params.jobPostingId) options.jobPostingId = params.jobPostingId
      return learningService.generateTopic(params.skillName, options)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
    },
  })
}

export function useCreateLearningFromJobMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: {
      jobPostingId: number
      jobTitle: string
      companyName?: string
      jobLocation?: string
      skillGaps: Array<{
        skillName: string
        currentLevel?: string
        requiredLevel: string
        importance?: string
      }>
    }) => learningService.createFromJobMatch(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
    },
  })
}

// --- Audio Generation ---

export function useGenerateAudio(topicId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => learningService.generateAudio(topicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.topicDetail(topicId) })
      queryClient.invalidateQueries({ queryKey: learningKeys.modules() })
    },
  })
}

// --- Q&A Hooks (Ask during lesson) ---

export function useQAHistory(moduleId: number) {
  return useQuery({
    queryKey: learningKeys.moduleQA(moduleId),
    queryFn: () => learningService.getQAHistory(moduleId),
    enabled: Number.isFinite(moduleId) && moduleId > 0,
  })
}

export function useAskQuestion(moduleId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      question,
      contextPosition,
    }: {
      question: string
      contextPosition?: ContextPosition
    }) => learningService.askQuestion(moduleId, question, contextPosition),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.moduleQA(moduleId) })
    },
  })
}

// --- Book Hooks ---

export function useBooks(category?: BookCategory, limit = 50) {
  return useInfiniteQuery({
    queryKey: learningKeys.bookList(category),
    queryFn: ({ pageParam = 0 }) => learningService.listBooks(category, pageParam, limit),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.offset + lastPage.limit : undefined,
  })
}

export function useBook(bookId: number) {
  return useQuery({
    queryKey: learningKeys.bookDetail(bookId),
    queryFn: () => learningService.getBook(bookId),
    enabled: Number.isFinite(bookId) && bookId > 0,
  })
}

export function usePersonalizeBook() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bookId: number) => learningService.personalizeBook(bookId),
    onSuccess: (_, bookId) => {
      queryClient.invalidateQueries({ queryKey: learningKeys.bookDetail(bookId) })
    },
  })
}

// --- Note Hooks ---

export function useNote(moduleId: number) {
  return useQuery({
    queryKey: learningKeys.moduleNote(moduleId),
    queryFn: () => learningService.getNote(moduleId),
    enabled: Number.isFinite(moduleId) && moduleId > 0,
  })
}

export function useSaveNote(moduleId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (content: string) => learningService.saveNote(moduleId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.moduleNote(moduleId) })
    },
  })
}

export function useDeleteNote(moduleId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => learningService.deleteNote(moduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.moduleNote(moduleId) })
    },
  })
}

// --- Podcast Hooks ---

export function usePodcasts(category?: PodcastCategory, limit = 50) {
  return useInfiniteQuery({
    queryKey: learningKeys.podcastList(category),
    queryFn: ({ pageParam = 0 }) => learningService.listPodcasts(category, pageParam, limit),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.offset + lastPage.limit : undefined,
  })
}

export function usePodcast(podcastId: number) {
  return useQuery({
    queryKey: learningKeys.podcastDetail(podcastId),
    queryFn: () => learningService.getPodcast(podcastId),
    enabled: Number.isFinite(podcastId) && podcastId > 0,
  })
}
